import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, ApiError } from '../lib/api.js';
import { useFormatters, useI18n } from '../i18n/LanguageProvider.js';

/**
 * Mail settings, and proof that they work.
 *
 * The test button is not a convenience. Saved settings with a wrong password
 * produce no error at all — the failure surfaces later, when somebody is locked
 * out and the code never arrives, which is the worst moment to learn about it.
 * So the screen distinguishes "saved" from "delivered", and says plainly which
 * of the two has happened.
 */
export function MailSettings() {
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const formatters = useFormatters();

  const smtp = useQuery({ queryKey: ['admin-smtp'], queryFn: api.adminSmtp });

  const [form, setForm] = useState({
    host: '',
    port: 587,
    username: '',
    password: '',
    fromAddress: '',
    fromName: 'Meta Pages Publisher',
    isActive: true,
  });
  const [testTo, setTestTo] = useState('');
  const [notice, setNotice] = useState<{
    kind: 'ok' | 'error';
    text: string;
  } | null>(null);

  // Fill the form once the stored settings arrive. The password is never sent
  // back by the API, so its field always starts empty and an empty value means
  // "keep the one you have".
  useEffect(() => {
    const current = smtp.data?.smtp;
    if (!current?.host) return;
    setForm((previous) => ({
      ...previous,
      host: current.host ?? '',
      port: current.port,
      username: current.username ?? '',
      fromAddress: current.fromAddress ?? '',
      fromName: current.fromName || 'Meta Pages Publisher',
      isActive: current.isActive,
    }));
    setTestTo((previous) => previous || (current.lastTestTo ?? ''));
  }, [smtp.data]);

  const save = useMutation({
    mutationFn: () =>
      api.updateSmtp({
        host: form.host,
        port: form.port,
        username: form.username || undefined,
        password: form.password || undefined,
        fromAddress: form.fromAddress,
        fromName: form.fromName,
        isActive: form.isActive,
      }),
    onSuccess: () => {
      setNotice({ kind: 'ok', text: t('mail.saved') });
      setForm((previous) => ({ ...previous, password: '' }));
      void queryClient.invalidateQueries({ queryKey: ['admin-smtp'] });
    },
    onError: (error) =>
      setNotice({
        kind: 'error',
        text: error instanceof ApiError ? error.message : t('common.requestFailed'),
      }),
  });

  const test = useMutation({
    mutationFn: () => api.testSmtp(testTo),
    onSuccess: (result) => {
      setNotice(
        result.ok
          ? { kind: 'ok', text: t('mail.testOk') }
          : { kind: 'error', text: `${t('mail.testFailed')} ${result.error}` },
      );
      void queryClient.invalidateQueries({ queryKey: ['admin-smtp'] });
    },
    onError: (error) =>
      setNotice({
        kind: 'error',
        text: error instanceof ApiError ? error.message : t('common.requestFailed'),
      }),
  });

  if (smtp.isLoading) return <div className="loading">{t('common.loading')}</div>;

  const current = smtp.data?.smtp;
  const proven = Boolean(current?.lastTestedAt && !current.lastTestError);

  return (
    <>
      <p className="muted small">{t('mail.hint')}</p>

      {/* Loud on purpose. Everything that lets somebody back into their account
          runs through this, and a quiet badge would be read as decoration. */}
      {!proven && (
        <div className="notice error">
          <strong>{t('mail.unprovenTitle')}</strong>
          <div className="small">{t('mail.unprovenBody')}</div>
        </div>
      )}

      {notice && (
        <div className={notice.kind === 'ok' ? 'notice ok' : 'notice error'}>{notice.text}</div>
      )}

      <div className="card composer">
        <h3>{t('mail.settings')}</h3>

        <div className="plan-fields">
          <div>
            <label htmlFor="smtp-host">{t('mail.host')}</label>
            <input
              id="smtp-host"
              className="ltr"
              placeholder="smtp.gmail.com"
              value={form.host}
              onChange={(event) => setForm({ ...form, host: event.target.value })}
            />
          </div>
          <div>
            <label htmlFor="smtp-port">{t('mail.port')}</label>
            <input
              id="smtp-port"
              type="number"
              className="ltr"
              value={form.port}
              onChange={(event) => setForm({ ...form, port: Number(event.target.value) })}
            />
          </div>
        </div>

        <label htmlFor="smtp-user">{t('mail.username')}</label>
        <input
          id="smtp-user"
          className="ltr"
          placeholder="mailer@yourdomain.com"
          value={form.username}
          onChange={(event) => setForm({ ...form, username: event.target.value })}
        />

        <label htmlFor="smtp-pass">
          {t('mail.password')}
          {current?.hasPassword && (
            <span className="tiny muted"> — {t('mail.passwordStored')}</span>
          )}
        </label>
        <input
          id="smtp-pass"
          type="password"
          className="ltr"
          autoComplete="new-password"
          placeholder={current?.hasPassword ? '••••••••••••' : ''}
          value={form.password}
          onChange={(event) => setForm({ ...form, password: event.target.value })}
        />
        <p className="tiny muted">{t('mail.appPasswordHint')}</p>

        <div className="plan-fields">
          <div>
            <label htmlFor="smtp-from">{t('mail.fromAddress')}</label>
            <input
              id="smtp-from"
              className="ltr"
              value={form.fromAddress}
              onChange={(event) => setForm({ ...form, fromAddress: event.target.value })}
            />
          </div>
          <div>
            <label htmlFor="smtp-fromname">{t('mail.fromName')}</label>
            <input
              id="smtp-fromname"
              value={form.fromName}
              onChange={(event) => setForm({ ...form, fromName: event.target.value })}
            />
          </div>
        </div>

        <label className="checkbox-row">
          <input
            type="checkbox"
            checked={form.isActive}
            onChange={(event) => setForm({ ...form, isActive: event.target.checked })}
          />
          {t('mail.active')}
        </label>

        <button
          type="button"
          className="primary"
          disabled={save.isPending || !form.host || !form.fromAddress}
          onClick={() => save.mutate()}
        >
          {t('common.save')}
        </button>
      </div>

      <div className="card composer">
        <h3>{t('mail.test')}</h3>
        <p className="muted small">{t('mail.testHint')}</p>

        <div className="field-with-action">
          <input
            className="ltr"
            type="email"
            placeholder="you@example.com"
            value={testTo}
            onChange={(event) => setTestTo(event.target.value)}
          />
          <button
            type="button"
            className="secondary"
            disabled={test.isPending || !testTo.includes('@')}
            onClick={() => test.mutate()}
          >
            {test.isPending ? t('mail.sending') : t('mail.sendTest')}
          </button>
        </div>

        {current?.lastTestedAt && (
          <p className={current.lastTestError ? 'error small' : 'ok small'}>
            {current.lastTestError
              ? `${formatters.dateTime(current.lastTestedAt)} — ${current.lastTestError}`
              : `${formatters.dateTime(current.lastTestedAt)} — ${t('mail.lastTestOk')}`}
          </p>
        )}
      </div>
    </>
  );
}
